import { fabric } from "fabric";

/**
 * The floating "delete" control rendered above a selected annotation, plus the
 * hit-testing/removal logic that backs it. Mixed into BboxCanvas.prototype.
 */
export default {
    setupDeleteControl() {
        const canvasWrapper = this;

        const invokeDeleteControl = (eventData, transform) => {
            if (!transform || !transform.target) {
                const active = canvasWrapper.canvas && canvasWrapper.canvas.getActiveObject();
                if (!active) {
                    return;
                }
                transform = { target: active, corner: 'deleteControl' };
            } else if (!transform.corner) {
                transform.corner = 'deleteControl';
            }
            canvasWrapper.deleteObject2(eventData, transform);
        };

        const deleteControlDef = new fabric.Control({
            x: 0.5,
            y: -0.5,
            offsetY: 24,
            cursorStyle: 'pointer',
            cornerSize: 28,
            touchSizeX: 36,
            touchSizeY: 36,
            mouseDownHandler(eventData, transform) {
                invokeDeleteControl(eventData, transform);
                return true;
            },
            actionHandler() {
                return false;
            },
            mouseUpHandler() {
                return true;
            },
            render(ctx, left, top, styleOverride, fabricObject) {
                canvasWrapper.renderIcon(ctx, left, top, styleOverride, fabricObject, this.cornerSize);
            },
        });
        fabric.Object.prototype.controls.deleteControl = deleteControlDef;
        this._deleteControlDef = deleteControlDef;
    },

    ensureDeleteControlOnObject(obj) {
        if (!obj || !obj.controls) {
            return;
        }
        if (!obj.controls.deleteControl && this._deleteControlDef) {
            obj.controls.deleteControl = this._deleteControlDef;
        }
        if (typeof obj.setControlVisible === 'function') {
            obj.setControlVisible('deleteControl', true);
        }
    },

    _deleteControlHitRadius(forTouch) {
        return forTouch ? 36 : 32;
    },

    _pointerHitsDeleteControl(obj, pointer, forTouch) {
        if (!obj || !obj.oCoords || !obj.oCoords.deleteControl) {
            return false;
        }
        const o = obj.oCoords.deleteControl;
        const poly = forTouch && o.touchCorner ? o.touchCorner : o.corner;

        // After setCoords(), corner is a quad {tl,tr,br,bl} — not {x,y}.
        if (poly && poly.tl && typeof obj._getImageLines === 'function' && typeof obj._findCrossPoints === 'function') {
            const lines = obj._getImageLines(poly);
            const xPoints = obj._findCrossPoints({ x: pointer.x, y: pointer.y }, lines);
            return xPoints !== 0 && xPoints % 2 === 1;
        }

        // Fallback: center from positionHandler coords (o.x / o.y).
        if (typeof o.x === 'number' && typeof o.y === 'number') {
            const r = this._deleteControlHitRadius(forTouch);
            const dx = pointer.x - o.x;
            const dy = pointer.y - o.y;
            return (dx * dx + dy * dy) <= (r * r);
        }

        return false;
    },

    _objectDeleteCornerAtPointer(obj, pointer, forTouch) {
        if (!obj || !obj.hasControls || !obj.canvas || this.canvas.readOnly) {
            return null;
        }
        this.ensureDeleteControlOnObject(obj);
        obj.setCoords();
        if (typeof obj._findTargetCorner === 'function') {
            const corner = obj._findTargetCorner(pointer, forTouch);
            if (corner) {
                return corner;
            }
        }
        return this._pointerHitsDeleteControl(obj, pointer, forTouch) ? 'deleteControl' : null;
    },

    findObjectAtDeleteControl(pointer, forTouch) {
        const objects = this.canvas.getObjects();
        const prevActive = this.canvas.getActiveObject();

        for (let i = objects.length - 1; i >= 0; i--) {
            const obj = objects[i];
            if (!obj.annotation_id || obj.text_box || obj.placementGuide || !obj.selectable) {
                continue;
            }
            if (!obj.hasControls) {
                continue;
            }

            this.canvas.setActiveObject(obj);
            const corner = this._objectDeleteCornerAtPointer(obj, pointer, forTouch);
            if (corner === 'deleteControl') {
                return obj;
            }
        }

        if (prevActive) {
            this.canvas.setActiveObject(prevActive);
            prevActive.setCoords();
        } else {
            this.canvas.discardActiveObject();
        }
        return null;
    },

    tryHandleDeleteControlClick(opt) {
        if (!this.canvas || this.canvas.readOnly || this._deleteClickHandled) {
            return false;
        }

        const pointer = this.canvas.getPointer(opt.e, true);
        const forTouch = fabric.util.isTouchEvent(opt.e);
        const active = this.canvas.getActiveObject();

        let target = null;

        const isDeleteHit = (obj) => {
            if (!obj || !obj.annotation_id || obj.text_box || obj.placementGuide) {
                return false;
            }
            this.ensureDeleteControlOnObject(obj);
            obj.setCoords();
            return this._pointerHitsDeleteControl(obj, pointer, forTouch);
        };

        if (active && isDeleteHit(active)) {
            target = active;
        } else if (opt.target && opt.target !== active && isDeleteHit(opt.target)) {
            target = opt.target;
        }

        if (!target) {
            return false;
        }

        if (opt.e) {
            if (opt.e.preventDefault) {
                opt.e.preventDefault();
            }
            if (opt.e.stopPropagation) {
                opt.e.stopPropagation();
            }
        }
        this._deleteClickHandled = true;
        this.canvas.setActiveObject(target);
        this.deleteObject2(opt.e, { target, corner: 'deleteControl' });
        const this2 = this;
        setTimeout(function () {
            this2._deleteClickHandled = false;
        }, 0);
        return true;
    },

    isDeleteControlTransform(transform) {
        if (!transform) {
            return false;
        }
        const corner = String(transform.corner || transform.key || '');
        return corner === 'deleteControl' || corner.includes('delete');
    },

    isFabricTargetDeletable(target, canvas) {
        if (!target || !canvas || canvas.readOnly) {
            return false;
        }
        if (target.placementGuide || target.text_box) {
            return false;
        }

        const active = canvas.getActiveObject ? canvas.getActiveObject() : null;
        if (active === target) {
            return true;
        }
        if (active && active._objects && active._objects.includes(target)) {
            return true;
        }
        if (target._objects && Array.isArray(target._objects)) {
            return target._objects.some((obj) => obj && obj.annotation_id);
        }

        return !!target.annotation_id;
    },

    deleteObject2(eventData, transform) {
        var target = transform && transform.target;
        var canvas = target && target.canvas;

        // Check if target and canvas exist
        if (!target || !canvas) {
            return;
        }

        if (!this.isFabricTargetDeletable(target, canvas)) {
            return;
        }

        const isDeleteControl = this.isDeleteControlTransform(transform);
        if (isDeleteControl) {
            target.onTheMove = false;
        }

        const canvasId = canvas.canvasId || this.canvasId;
        const deleteEventName = canvasId + 'delete_annotation';

        if(target._objects && Array.isArray(target._objects)) {
            target._objects.forEach(obj => {
                if (!obj) {
                    return;
                }
                if (!isDeleteControl && obj.onTheMove) {
                    return;
                }
                obj.onTheMove = false;

                if (canvas && canvas.remove && obj) {
                    try {
                        canvas.remove(obj);
                        if (obj.text && obj.text.canvas) canvas.remove(obj.text);
                        if (obj.inner_point && obj.inner_point.canvas) canvas.remove(obj.inner_point);
                    } catch (error) {
                        console.warn('Error removing object from canvas:', error);
                    }
                }

                if (obj.annotation_id) {
                    window.dispatchEvent(new CustomEvent(deleteEventName, {
                        detail: {
                            id: obj.annotation_id,
                        }
                    }));
                }
            });

            if (canvas && canvas.discardActiveObject) {
                canvas.discardActiveObject();
            }

            if (canvas && canvas.requestRenderAll) {
                canvas.requestRenderAll();
            }
        }
        else {
            if (!isDeleteControl && target.onTheMove) {
                return;
            }
            target.onTheMove = false;

            if (canvas && canvas.remove && target) {
                try {
                    canvas.remove(target);
                    if (target.text && target.text.canvas) canvas.remove(target.text);
                    if (target.inner_point && target.inner_point.canvas) canvas.remove(target.inner_point);
                } catch (error) {
                    console.warn('Error removing target from canvas:', error);
                }
            }

            if (canvas && canvas.requestRenderAll) {
                canvas.requestRenderAll();
            }

            // Remove tooltip if the deleted annotation was being hovered
            if (canvas.tooltip) {
                canvas.remove(canvas.tooltip);
                canvas.tooltip = null;
            }
            if (canvas.tooltipText) {
                canvas.remove(canvas.tooltipText);
                canvas.tooltipText = null;
            }

            if (target.annotation_id) {
                window.dispatchEvent(new CustomEvent(deleteEventName, {
                    detail: {
                        id: target.annotation_id,
                    }
                }));
            }
        }
    },

    renderIcon(ctx, left, top, styleOverride, fabricObject, cornerSize) {
        if (fabricObject.onTheMove) {
            return;
        }

        let size = cornerSize || this.cornerSize || 24;
        ctx.save();
        ctx.translate(left, top);
        ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
        ctx.drawImage(window.deleteIconImg, -size / 2, -size / 2, size, size);
        ctx.restore();
    },
};
